import { ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import closeSvg from 'assets/icons/close.svg';
import removeSvg from 'assets/icons/remove.svg';
import rubberSvg from 'assets/icons/rubber.svg';
import plusSvg from 'assets/icons/plus.svg';
import copyIcon from 'assets/icons/copy.svg';
import saveSvg from 'assets/icons/save.svg';

import Modal from 'lib/Modal';
import Button, { Color, Size } from 'lib/Button';
import usePendoTracking from 'customHooks/usePendoTracking';

import { clearForm, createNewLineWithUniqueName, duplicateLine, resetForm } from 'store/dealManagement/reducer';

import { getActionConditions } from 'store/dealManagement/selectors';
import { FrontEndType } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import TerminateDialog from './TerminateDialog';
import LiveLineDialog from './LiveLineDialog';
import IconButton from '../IconButton';
import { DspNoLongerInUseDialog } from './DspNoLongerInUse/DspNoLongerInUse';
import { useOnDspInUse } from './DspNoLongerInUse/useOnDspInUse';

const shouldRenderButton = (condition: boolean, element: ReactElement): ReactElement | null =>
  condition ? element : null;

interface LineActionsProps {
  createLine?: () => void;
  updateLine?: () => void;
  updateLiveLine?: () => void;
  deleteLine?: () => void;
  terminateLine?: () => void;
}

const LineActions = ({
  createLine = () => null,
  updateLine = () => null,
  updateLiveLine = () => null,
  deleteLine = () => null,
  terminateLine = () => null,
}: LineActionsProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { onDspInUse } = useOnDspInUse();
  const { getPendoDataId } = usePendoTracking();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showLiveLineModal, setShowLiveLineModal] = useState(false);
  const dealManagementState = useSelector((state: Store) => state.dealManagement);
  const {
    commonDeal: { currentLine },
  } = dealManagementState;
  const actionConditions = useSelector(getActionConditions);

  const handleTerminateLine = (): void => {
    setShowTerminateModal(false);
    terminateLine();
  };

  const handleDeleteLine = (): void => {
    setShowDeleteModal(false);
    deleteLine();
  };

  const handleSaveLine = (): void => {
    if (currentLine.id) {
      updateLine();
      return;
    }

    createLine();
  };

  const handleUpdateLiveLine = (): void => {
    setShowLiveLineModal(false);
    updateLiveLine();
  };

  return (
    <>
      {actionConditions.terminateLine.show || actionConditions.deleteLine.show ? (
        <div className="px-6 flex items-center">
          {shouldRenderButton(
            actionConditions.terminateLine.show,
            <IconButton
              icon={closeSvg}
              dataTestId="terminate-line-button"
              tooltipText="Terminate deal line"
              isDisabled={actionConditions.terminateLine.disabled}
              onClick={() => setShowTerminateModal(true)}
            />,
          )}
          {shouldRenderButton(
            actionConditions.deleteLine.show,
            <IconButton
              dataTestId="delete-line-button"
              icon={removeSvg}
              tooltipText="Delete deal line"
              isDisabled={actionConditions.deleteLine.disabled}
              onClick={() => setShowDeleteModal(true)}
            />,
          )}
        </div>
      ) : null}
      <div className="px-6 space-x-3 flex items-center">
        {shouldRenderButton(
          actionConditions.clearForm.show,
          <IconButton
            dataTestId="clear-form-button"
            icon={rubberSvg}
            tooltipText="Clear form"
            isDisabled={actionConditions.clearForm.disabled}
            onClick={() => dispatch(clearForm())}
          />,
        )}
        {shouldRenderButton(
          actionConditions.resetForm.show,
          <IconButton
            dataTestId="reset-form-button"
            icon={rubberSvg}
            tooltipText="Reset form"
            isDisabled={actionConditions.resetForm.disabled}
            onClick={() => dispatch(resetForm())}
          />,
        )}
        {shouldRenderButton(
          actionConditions.createLine.show,
          <IconButton
            dataTestId="create-line-button"
            pendoId={getPendoDataId('Create line')}
            icon={plusSvg}
            tooltipText="Create deal line"
            isDisabled={actionConditions.createLine.disabled}
            onClick={() => {
              onDspInUse(() => dispatch(createNewLineWithUniqueName(FrontEndType.STANDARD)));
            }}
          />,
        )}
        {shouldRenderButton(
          actionConditions.duplicateLine.show,
          <IconButton
            dataTestId="duplicate-line-button"
            pendoId={getPendoDataId('Duplicate line')}
            icon={copyIcon}
            tooltipText="Duplicate deal line"
            isDisabled={actionConditions.duplicateLine.disabled}
            onClick={() => {
              onDspInUse(() => dispatch(duplicateLine(currentLine.id)));
            }}
          />,
        )}
        {shouldRenderButton(
          actionConditions.saveLine.show,
          <IconButton
            dataTestId="line-save-button"
            icon={saveSvg}
            tooltipText="Save deal line"
            isDisabled={actionConditions.saveLine.disabled}
            onClick={handleSaveLine}
          />,
        )}
      </div>
      <TerminateDialog
        isTerminateModalVisible={showTerminateModal}
        setShowTerminateModal={setShowTerminateModal}
        handleTerminateLine={handleTerminateLine}
      />
      <LiveLineDialog
        isLiveLineModalVisible={showLiveLineModal}
        setShowLiveLineModal={setShowLiveLineModal}
        handleUpdateLiveLine={handleUpdateLiveLine}
      />
      <Modal
        isOpen={showDeleteModal}
        icon={removeSvg}
        title={`Delete ${currentLine.name}?`}
        actionButtons={
          <>
            <Button color={Color.PRIMARY} size={Size.LARGE} onClick={() => setShowDeleteModal(false)} label="Cancel" />
            <Button color={Color.DANGER} size={Size.LARGE} onClick={handleDeleteLine} label="Delete" />
          </>
        }
      >
        <div className="ml-8 body-base text-neutral-950-opacity-60">
          <p className="mb-2">
            Deleting <strong>{currentLine.name}</strong> will remove it permanently and you will be unable to undo this
            change.
          </p>
          <p>
            Are you sure you want to delete <strong>{currentLine.name}</strong>?
          </p>
        </div>
      </Modal>
      <DspNoLongerInUseDialog />
    </>
  );
};

export default LineActions;
